import { useEffect, useId, useMemo, useRef, useState } from 'react'

export interface SearchableSelectOption {
  value: string
  label: string
  sublabel?: string
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value: string
  onChange: (value: string) => void
  /** Text of the pinned reset option and of the button when value is '' or unresolved. */
  allLabel: string
  searchPlaceholder?: string
  /** Placed on the toggle button so existing tests can keep their testid handle. */
  testId?: string
  /** Override the wrapper width; `relative` is always applied. */
  className?: string
}

/**
 * Generic searchable dropdown filter. Modeled on PhonePickerCombobox but
 * domain-agnostic and with a pinned «all» (reset) option, so it can drive
 * filters that must return to the empty value ''. Filters options by label
 * OR sublabel (case-insensitive substring).
 *
 * The pinned «all» option (value '') is always matches[0] — never filtered
 * out — so there is always a way to clear the filter and `matches` is never
 * empty.
 *
 * Keyboard: ArrowUp / ArrowDown move the active option, Enter selects it,
 * Escape closes. `aria-activedescendant` mirrors the focused option id.
 *
 * Layout / overflow (.claude/rules/web-layout.md): the panel is absolute
 * inside a `relative` wrapper. Used in the /staff/calls filter bar — no
 * `overflow: hidden` ancestor on that path. If a parent later gains an
 * overflow boundary, switch to `createPortal`. z-50 matches the project's
 * popover convention.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  allLabel,
  searchPlaceholder = 'Поиск',
  testId,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listboxId = useId()
  const optionIdPrefix = useId()

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  )

  // Pinned «all» (value '') is always first and never filtered → matches[0].
  const matches = useMemo<SearchableSelectOption[]>(() => {
    const all: SearchableSelectOption = { value: '', label: allLabel }
    const q = query.trim().toLowerCase()
    if (!q) return [all, ...options]
    return [
      all,
      ...options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.sublabel ?? '').toLowerCase().includes(q),
      ),
    ]
  }, [options, query, allLabel])

  // Reset the active index to 0 at the event handlers that change the
  // filter (open toggle, query change, selection) so aria-activedescendant
  // always points at the first match after a narrow — keeping state writes
  // out of an effect body per react-hooks/set-state-in-effect. The read-time
  // clamp is the backstop: it keeps the index in range between those resets.
  const safeActiveIndex = Math.min(activeIndex, Math.max(0, matches.length - 1))

  // Close on outside click. Touch-friendly: react to `mousedown` /
  // `touchstart`, not `click`, so a tap that starts outside the popover
  // closes it before the focus shifts.
  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent | TouchEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
    }
  }, [open])

  // `selected` is null when value is '' (the all-state) or unresolved (e.g.
  // a ?phone= seed before the options list loads) → button falls back to allLabel.
  const buttonLabel = selected
    ? `${selected.label}${selected.sublabel ? ` (${selected.sublabel})` : ''}`
    : allLabel

  function pick(v: string) {
    onChange(v)
    setOpen(false)
    setQuery('')
    setActiveIndex(0)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setOpen(true)
        setActiveIndex(0)
      }
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      setQuery('')
      setActiveIndex(0)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (matches.length === 0 ? 0 : (i + 1) % matches.length))
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) =>
        matches.length === 0 ? 0 : (i - 1 + matches.length) % matches.length,
      )
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const target = matches[safeActiveIndex]
      if (target) pick(target.value)
    }
  }

  const activeOptionId =
    open && matches[safeActiveIndex]
      ? `${optionIdPrefix}-${matches[safeActiveIndex].value}`
      : undefined

  return (
    <div
      className={`relative ${className ?? 'w-48 sm:w-56'}`}
      ref={wrapperRef}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        data-testid={testId}
        onClick={() => {
          setOpen((v) => !v)
          setQuery('')
          setActiveIndex(0)
        }}
        className="w-full flex items-center justify-between gap-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-left text-gray-900 dark:text-gray-100 hover:border-gray-400 dark:hover:border-gray-600"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={activeOptionId}
      >
        <span
          className={`truncate ${selected ? '' : 'text-gray-500 dark:text-gray-400'}`}
        >
          {buttonLabel}
        </span>
        <span aria-hidden className="shrink-0 text-gray-400">
          ▾
        </span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
          <div className="p-2 border-b border-gray-100 dark:border-gray-800">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setActiveIndex(0)
              }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm text-gray-900 dark:text-gray-100"
            />
          </div>
          <div
            id={listboxId}
            role="listbox"
            className="max-h-64 overflow-y-auto py-1"
          >
            {matches.map((o, i) => {
              const isSelected = o.value === value
              const isActive = i === safeActiveIndex
              return (
                <div
                  key={o.value}
                  id={`${optionIdPrefix}-${o.value}`}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => pick(o.value)}
                  className={`cursor-pointer px-3 py-2 text-sm ${
                    isSelected
                      ? 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300'
                      : isActive
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                        : 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="font-medium leading-tight">{o.label}</div>
                  {o.sublabel && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                      {o.sublabel}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
